import { search as fuzzySearch } from 'fast-fuzzy';
import { AppServiceResponse, listAppServices } from 'sync/app-service-service';
import { PerPageValue, QueryOptions } from 'sync/query';
import { matchLeftCharacters } from 'utils/strings';

type GetEntireAppServiceListProps = {
  startPage: number;
  perPage: PerPageValue;
  organizationId: string;
  sortBy?: string;
  sortDirection?: QueryOptions['sortDirection'];
};
export const getEntireAppServiceList = async ({
  startPage,
  perPage,
  organizationId,
  sortBy,
  sortDirection,
}: GetEntireAppServiceListProps) => {
  /**
   * Fetches projects with given page and organization identifier.
   */
  const getPage = async (page: number) => {
    try {
      return await listAppServices({ organizationId }, { page, perPage, sortBy, sortDirection });
    } catch {
      return null;
    }
  };

  const getNextPageRecords = async ({
    currentResponse,
    currentPage,
  }: {
    currentResponse: Awaited<ReturnType<typeof getPage>>;
    currentPage: number;
  }): Promise<AppServiceResponse[]> => {
    if (!currentResponse?.data?.length) {
      return [];
    }

    const nextPage = currentPage + 1;
    const nextResponse = await getPage(nextPage);
    const nextResults = await getNextPageRecords({ currentResponse: nextResponse, currentPage: nextPage });

    return [...currentResponse.data, ...nextResults];
  };

  const startPageResponse = await getPage(startPage);

  if (!startPageResponse) {
    return null;
  }

  const nextPageRecords = await getNextPageRecords({
    currentResponse: startPageResponse,
    currentPage: startPage,
  });

  return {
    ...startPageResponse,
    data: nextPageRecords,
  };
};

export const getSearchKeyAppServiceSelectors = () => {
  return [
    // Add a selector to search the AppService name
    {
      boost: 2,
      fn: (record: AppServiceResponse): string => {
        return record.data.name;
      },
    },

    // Add a selector to search database name associated with the
    // AppService
    {
      boost: 1,
      fn: (record: AppServiceResponse): string => {
        return record.data.cluster.name;
      },
    },
    {
      boost: 1,
      fn: (record: AppServiceResponse): string => {
        return record.data.config.provider;
      },
    },
    {
      boost: 1,
      fn: (record: AppServiceResponse): string => {
        return record.data.config.region;
      },
    },
    // Add a selector to allow search of the individual who created
    {
      boost: 1,
      fn: (record: AppServiceResponse): string => {
        return record.data.createdBy ?? '';
      },
    },
  ] as const;
};

type IncludeSearchSuggestionsProps = {
  appServiceList: AppServiceResponse[];
  addUniqueResult: (item: AppServiceResponse, rank: number) => void;
  searchTerm: string;
};

export const includeSearchAppServiceListSuggestions = ({ searchTerm, appServiceList, addUniqueResult }: IncludeSearchSuggestionsProps) => {
  // First, let's check to see if someone is searching for an exact match.
  appServiceList.forEach((record: AppServiceResponse) => {
    // If the search term looks like they just want to list app services
    // then we can give them all the app services.
    if (matchLeftCharacters(searchTerm, ['appservices', 'app services'])) {
      addUniqueResult(record, 1);
      return;
    }
    if (record.data.id.toLowerCase() === searchTerm) {
      addUniqueResult(record, 100);
      return;
    }
    // Check the Project ID?
    if (record.data.project.id.toLowerCase() === searchTerm) {
      addUniqueResult(record, 100);
      return;
    }
    // Check if someone is looking for the hostname?
    if (record.data.config && record.data.config.baseHostname === searchTerm) {
      addUniqueResult(record, 100);
    }
  });
};

type IncludeKeySelectorResultsAppServiceProps = {
  appServiceList: AppServiceResponse[];
  addUniqueResult: (item: AppServiceResponse, rank: number) => void;
  searchTerm: string;
  maxResults?: number;
  resultSet: Set<string>;
};

export const includeKeySelectorAppServiceResults = ({
  addUniqueResult,
  appServiceList,
  maxResults,
  resultSet,
  searchTerm,
}: IncludeKeySelectorResultsAppServiceProps) => {
  getSearchKeyAppServiceSelectors().forEach((keySelector) => {
    // We may not need to perform further, less relevant searches...
    if (maxResults && resultSet.size >= maxResults) {
      return;
    }

    const matches = fuzzySearch(searchTerm, appServiceList, {
      returnMatchData: true,
      keySelector: keySelector.fn,
    });

    matches.forEach((match) => {
      addUniqueResult(match.item, keySelector.boost + match.score);
    });
  });
};
